import React from "react";
import styled from "styled-components";
import Link from "next/link";
import { Heading } from "../ui/Heading";
import { ApiImage } from "../ui/ApiImage";
import { useIsBreakpoint } from "~/hooks/useIsBreakpoint";
import { formatDate } from "~/utils";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import LinkOverlay from "../styled/LinkOverlay";
import TextWithArrow from "../ui/TextWithArrow";
import SafeHtmlSpan from "../ui/SafeHtmlSpan";
import safeHtml from "~/utils/sanitize";
import { themeImgSizes, themeSpacePx } from "~/theme";

const imgSizesFeatured = {
  tablet: "50vw",
  base: "100vw",
};
const imgSizes = {
  ...["tabletLandscape", "desktop", "screen"].reduce(
    (carry: any, b: string) => ({
      [b]: themeSpacePx(b, 3),
      ...carry,
    }),
    {}
  ),
  ...imgSizesFeatured,
};

const Post = styled.article<{ featured: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;

  & .arrow {
    display: none;
  }

  ${({ theme }) => theme.breakpoints.tablet} {
    & .arrow {
      display: block;
      opacity: 0;
      transition: opacity 0.3s;
    }
  }

  & .apiimage.loaded {
    img {
      transition: filter 0.125s, opacity 0.5s;
    }
  }

  @media (any-pointer: fine) {
    &:hover {
      .apiimage.loaded .aspect-ratio {
        background-color: var(--ikon-hl-color, #ff0);

        img {
          filter: grayscale(1);
          mix-blend-mode: multiply;
        }
      }

      .arrow {
        opacity: 1;
      }
    }
  }
  
  ${({ theme }) => theme.breakpoints.tablet} {
    display: grid;
    grid-template-areas: "text img";
  }

  ${(props) =>
    props.theme.apply(
      ["tablet", "tabletLandscape", "desktop", "screen"],
      (breakpoint: string) => {
        return `
      
        grid-template-columns: 1fr ${
          breakpoint === "tablet"
            ? props.theme.spacePx(breakpoint, 3)
            : props.featured
            ? "1fr"
            : props.theme.spacePx(breakpoint, 2)
        };
        column-gap: ${props.theme.spacePx(breakpoint, 7)};
      
        `;
      }
    )}

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
      margin-bottom: ${props.theme.spacePx(breakpoint, 8)};
        `;
    })}
`;

const Img = styled.div`
  grid-area: img;
`;

const Text = styled.div`
  grid-area: text;
  width: 100%;
  overflow-x: hidden;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
      padding-top: ${props.theme.spacePx(breakpoint, 9)};
      padding-bottom: calc(${props.theme.spaceFontBottom(
        breakpoint,
        6,
        `body`
      )} - ${props.theme.spacePx(breakpoint, 8)});
        `;
    })}

  ${(props) =>
    props.theme.apply(["tablet", "desktop", "screen"], (breakpoint: string) => {
      return `
      border-top: 1px solid #000;      
        `;
    })}
`;

const PostHeader = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr auto;
`;

const Teaser = styled.div`
  > :last-child {
    margin-bottom: 0;
  }
`;


export const ListItemNews = ({
  post,
  featured,
}: {
  post: any;
  featured: boolean;
}) => {
  const { isMobile } = useIsBreakpoint();
  const config = useConfigContext();

  let type = post?.acf?.type?.trim() ? post?.acf?.type?.trim() : post?.taxonomies?.category?.[0]?.name;

  return (
    <Post featured={featured}>
      {post?.featuredImage?.sizes && (
        <Img>
          <ApiImage
            alt=""
            imgSizes={themeImgSizes(featured ? imgSizesFeatured : imgSizes)}
            sizes={post?.featuredImage?.sizes}
            ratio={!isMobile ? "1 / 1" : undefined}
            mode="constrain"
            objectPosition={post?.featuredImage?.cropPosition}
          />
        </Img>
      )}
      <Text>
        <PostHeader>
          <Heading heading="h4" space={8}>
            <SafeHtmlSpan html={type ?? "News"} />
            &emsp;
            <span style={{ fontWeight: 400 }}>{formatDate(post?.date)}</span>
          </Heading>

          <Heading heading="h4" space={8} className="arrow">
            <TextWithArrow align="right" heading="h4" size={9}>
              Read More
            </TextWithArrow>
          </Heading>
        </PostHeader>
        <Link passHref href={`${config.baseUrl}/news/view/${post?.slug}`}>
          <LinkOverlay>
            <Heading
              heading={featured ? "h1" : "h2"}
              space={!(featured && post?.acf?.sub_title?.trim()) ? 7 : 8}
              html={post?.title}
            />
          </LinkOverlay>
        </Link>
        {featured && post?.acf?.sub_title?.trim() && (
          <Heading heading="h2" space={7} html={post?.acf?.sub_title} />
        )}
        <Teaser
          dangerouslySetInnerHTML={{
            __html: safeHtml(post?.acf?.teaser),
          }}
        />
      </Text>
    </Post>
  );
};
